import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { getProducts } from '../../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Check, X, ArrowLeftRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ProductComparator = ({ isOpen, onClose, initialProduct }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [similarProducts, setSimilarProducts] = useState([]);

  // Reset and fetch similar products when dialog opens or initialProduct changes
  useEffect(() => {
    if (isOpen && initialProduct) {
      // RESET STATE: Start with empty selection - user must select products manually
      setSelectedProducts([]);
      
      const fetchSimilarProducts = async () => {
        try {
          const allProducts = await getProducts(initialProduct.category);
          // Include ALL products from the category (including initialProduct)
          setSimilarProducts(allProducts);
        } catch (error) {
          console.error('Error loading similar products:', error);
        }
      };
      fetchSimilarProducts();
    }
  }, [isOpen, initialProduct]);

  // CLEANUP: Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedProducts([]);
      setSimilarProducts([]);
    }
  }, [isOpen]);

  const handleProductSelect = (product) => {
    // Check if product is already selected
    const isSelected = selectedProducts.find(p => p.id === product.id);
    
    if (isSelected) {
      // Deselect: Remove product from selection
      setSelectedProducts(selectedProducts.filter(p => p.id !== product.id));
    } else {
      // Select: Add product only if less than 3 selected AND product not already in list
      if (selectedProducts.length < 3 && !selectedProducts.find(p => p.id === product.id)) {
        setSelectedProducts([...selectedProducts, product]);
      }
    }
  };

  const handleViewProduct = (productId) => {
    navigate(`/product/${productId}`);
    onClose();
  };

  // Get category-specific comparison rows
  const getComparisonRows = () => {
    if (!initialProduct) return [];

    const category = initialProduct.category;

    if (category === 'televisores') {
      return [
        { 
          label: t('size') || 'Tamaño', 
          getValue: (p) => {
            const match = p.name.match(/(\d+)"/);
            return match ? `${match[1]} pulgadas` : 'N/A';
          }
        },
        { 
          label: t('brand') || 'Marca', 
          getValue: (p) => p.brand 
        },
        { 
          label: t('resolution') || 'Resolución', 
          getValue: (p) => {
            if (p.features.some(f => f.includes('4K') || f.includes('UHD'))) return '4K Ultra HD';
            if (p.features.some(f => f.includes('Full HD'))) return 'Full HD';
            if (p.features.some(f => f.includes('HD'))) return 'HD Ready';
            return 'N/A';
          }
        },
        { 
          label: t('panel') || 'Panel', 
          getValue: (p) => {
            if (p.features.some(f => f.includes('QLED'))) return 'QLED';
            if (p.features.some(f => f.includes('OLED'))) return 'OLED';
            if (p.features.some(f => f.includes('NanoCell'))) return 'NanoCell';
            if (p.features.some(f => f.includes('Crystal'))) return 'Crystal UHD';
            return 'LED';
          }
        },
        { 
          label: t('system') || 'Sistema', 
          getValue: (p) => {
            if (p.features.some(f => f.includes('Google TV'))) return 'Google TV';
            if (p.features.some(f => f.includes('Tizen'))) return 'Tizen OS';
            if (p.features.some(f => f.includes('webOS'))) return 'webOS';
            return 'Smart TV';
          }
        },
        {
          label: t('price') || 'Precio',
          getValue: (p) => `RD$${p.price.toLocaleString()}`
        },
        {
          label: t('financing') || 'Financiamiento',
          getValue: (p) => {
            const monthly = p.monthly_payment;
            return `Desde RD$${monthly.toLocaleString()}/mes`;
          }
        },
        { 
          label: t('warranty') || 'Garantía', 
          getValue: (p) => p.warranty 
        }
      ];
    } else if (category === 'aires') {
      return [
        {
          label: 'BTU',
          getValue: (p) => {
            const match = p.name.match(/(\d+,?\d*)\s*BTU/);
            return match ? match[1] + ' BTU' : 'N/A';
          }
        },
        {
          label: t('brand') || 'Marca',
          getValue: (p) => p.brand
        },
        {
          label: t('type') || 'Tipo',
          getValue: (p) => {
            if (p.features.some(f => f.toLowerCase().includes('inverter'))) return 'Inverter';
            return 'Convencional';
          }
        },
        {
          label: t('coverage') || 'Área',
          getValue: (p) => {
            const btu = p.name.match(/(\d+),?(\d+)/);
            if (btu) {
              const btuNum = parseInt(btu[1] + (btu[2] || ''));
              if (btuNum >= 24000) return 'Grande (>30m²)';
              if (btuNum >= 18000) return 'Mediano (20-30m²)';
              return 'Pequeño (<20m²)';
            }
            return 'N/A';
          }
        },
        {
          label: t('consumption') || 'Consumo',
          getValue: (p) => {
            if (p.features.some(f => f.toLowerCase().includes('inverter'))) return 'Bajo (Inverter)';
            if (p.features.some(f => f.toLowerCase().includes('ahorro'))) return 'Eficiente';
            return 'Estándar';
          }
        },
        {
          label: t('price') || 'Precio',
          getValue: (p) => `RD$${p.price.toLocaleString()}`
        },
        {
          label: t('financing') || 'Financiamiento',
          getValue: (p) => {
            const monthly = p.monthly_payment;
            return `Desde RD$${monthly.toLocaleString()}/mes`;
          }
        },
        {
          label: t('warranty') || 'Garantía',
          getValue: (p) => p.warranty
        }
      ];
    } else if (category === 'electrodomesticos') {
      // Check if it's a blender based on product name
      const isBlender = initialProduct.name.toLowerCase().includes('licuadora');
      
      if (isBlender) {
        return [
          {
            label: t('power') || 'Potencia',
            getValue: (p) => {
              const powerMatch = p.features.find(f => f.includes('W') || f.toLowerCase().includes('motor'));
              return powerMatch || 'N/A';
            }
          },
          {
            label: t('brand') || 'Marca',
            getValue: (p) => p.brand
          },
          {
            label: t('capacity') || 'Capacidad',
            getValue: (p) => {
              const capMatch = p.features.find(f => f.toLowerCase().includes('litro'));
              return capMatch || 'N/A';
            }
          },
          {
            label: t('speeds') || 'Velocidades',
            getValue: (p) => {
              const speedMatch = p.features.find(f => f.toLowerCase().includes('velocidad'));
              return speedMatch || 'N/A';
            }
          },
          {
            label: t('material') || 'Material',
            getValue: (p) => {
              if (p.features.some(f => f.toLowerCase().includes('vidrio'))) return 'Vidrio';
              if (p.features.some(f => f.toLowerCase().includes('acero'))) return 'Acero';
              return 'Plástico';
            }
          },
          {
            label: t('price') || 'Precio',
            getValue: (p) => `RD$${p.price.toLocaleString()}`
          },
          {
            label: t('financing') || 'Financiamiento',
            getValue: (p) => {
              const monthly = p.monthly_payment;
              return `Desde RD$${monthly.toLocaleString()}/mes`;
            }
          },
          {
            label: t('warranty') || 'Garantía',
            getValue: (p) => p.warranty
          }
        ];
      } else {
        // General appliances
        return [
          {
            label: t('brand') || 'Marca',
            getValue: (p) => p.brand
          },
          {
            label: t('type') || 'Tipo',
            getValue: (p) => {
              if (p.name.toLowerCase().includes('microondas')) return 'Microondas';
              if (p.name.toLowerCase().includes('dispensador')) return 'Dispensador';
              if (p.name.toLowerCase().includes('sandwichera')) return 'Sandwichera';
              return 'Electrodoméstico';
            }
          },
          {
            label: t('features') || 'Características',
            getValue: (p) => p.features.slice(0, 2).join(', ')
          },
          {
            label: t('price') || 'Precio',
            getValue: (p) => `RD$${p.price.toLocaleString()}`
          },
          {
            label: t('financing') || 'Financiamiento',
            getValue: (p) => {
              const monthly = p.monthly_payment;
              return `Desde RD$${monthly.toLocaleString()}/mes`;
            }
          },
          {
            label: t('warranty') || 'Garantía',
            getValue: (p) => p.warranty
          }
        ];
      }
    } else if (category === 'abanicos') {
      return [
        {
          label: t('type') || 'Tipo',
          getValue: (p) => {
            if (p.name.toLowerCase().includes('pared')) return 'Pared';
            if (p.name.toLowerCase().includes('pedestal')) return 'Pedestal';
            return 'Abanico';
          }
        },
        {
          label: t('brand') || 'Marca',
          getValue: (p) => p.brand
        },
        {
          label: t('speeds') || 'Velocidades',
          getValue: (p) => {
            const speedMatch = p.features.find(f => f.toLowerCase().includes('velocidad'));
            return speedMatch || 'N/A';
          }
        },
        {
          label: t('power') || 'Potencia',
          getValue: (p) => {
            if (p.features.some(f => f.toLowerCase().includes('silencioso'))) return 'Silencioso';
            return 'Estándar';
          }
        },
        {
          label: t('price') || 'Precio',
          getValue: (p) => `RD$${p.price.toLocaleString()}`
        },
        {
          label: t('financing') || 'Financiamiento',
          getValue: (p) => {
            const monthly = p.monthly_payment;
            return `Desde RD$${monthly.toLocaleString()}/mes`;
          }
        },
        {
          label: t('warranty') || 'Garantía',
          getValue: (p) => p.warranty
        }
      ];
    } else if (category === 'audio') {
      return [
        {
          label: t('brand') || 'Marca',
          getValue: (p) => p.brand
        },
        {
          label: t('power') || 'Potencia',
          getValue: (p) => {
            const audioMatch = p.features.find(f => f.includes('Dolby') || f.includes('Audio'));
            return audioMatch || 'N/A';
          }
        },
        {
          label: t('connectivity') || 'Conectividad',
          getValue: (p) => {
            const connections = [];
            if (p.features.some(f => f.includes('Bluetooth'))) connections.push('Bluetooth');
            if (p.features.some(f => f.includes('HDMI'))) connections.push('HDMI');
            return connections.length > 0 ? connections.join(', ') : 'N/A';
          }
        },
        {
          label: t('price') || 'Precio',
          getValue: (p) => `RD$${p.price.toLocaleString()}`
        },
        {
          label: t('financing') || 'Financiamiento',
          getValue: (p) => {
            const monthly = p.monthly_payment;
            return `Desde RD$${monthly.toLocaleString()}/mes`;
          }
        },
        {
          label: t('warranty') || 'Garantía',
          getValue: (p) => p.warranty
        }
      ];
    }

    // Default fallback
    return [
      {
        label: t('brand') || 'Marca',
        getValue: (p) => p.brand
      },
      {
        label: t('price') || 'Precio',
        getValue: (p) => `RD$${p.price.toLocaleString()}`
      },
      {
        label: t('financing') || 'Financiamiento',
        getValue: (p) => {
          const monthly = p.monthly_payment;
          return `Desde RD$${monthly.toLocaleString()}/mes`;
        }
      },
      {
        label: t('warranty') || 'Garantía',
        getValue: (p) => p.warranty
      }
    ];
  };

  const comparisonRows = getComparisonRows();

  // SAFETY: Remove any duplicate products before rendering
  const uniqueSelectedProducts = selectedProducts.filter((product, index, self) =>
    index === self.findIndex((p) => p.id === product.id)
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <ArrowLeftRight className="h-6 w-6" />
            Comparar modelos
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Product Selection */}
          {uniqueSelectedProducts.length < 3 && (
            <div>
              <h3 className="text-sm font-medium mb-3 text-muted-foreground">
                Selecciona hasta 3 productos para comparar ({uniqueSelectedProducts.length}/3)
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {similarProducts.slice(0, 8).map(product => (
                  <div
                    key={product.id}
                    onClick={() => handleProductSelect(product)}
                    className={`cursor-pointer rounded-lg border-2 p-3 transition-all hover:shadow-md ${
                      uniqueSelectedProducts.find(p => p.id === product.id)
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="w-full h-20 flex items-center justify-center bg-white rounded mb-2 p-1">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-contain object-center"
                      />
                    </div>
                    <p className="text-xs font-medium line-clamp-2">{product.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      RD${product.price.toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Comparison Table - Apple Style */}
          {uniqueSelectedProducts.length >= 2 && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-border">
                    <th className="text-left p-4 font-semibold w-48"></th>
                    {uniqueSelectedProducts.map(product => (
                      <th key={product.id} className="p-4 min-w-[220px]">
                        <div className="space-y-3">
                          <div className="w-full h-32 flex items-center justify-center bg-white rounded-lg p-2">
                            <img
                              src={product.image}
                              alt={product.name}
                              className="w-full h-full object-contain object-center"
                            />
                          </div>
                          <div className="space-y-2">
                            <p className="text-base font-semibold text-left">{product.name}</p>
                            <Badge variant="secondary" className="text-xs">
                              {product.brand}
                            </Badge>
                          </div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row, index) => (
                    <tr 
                      key={index} 
                      className={`border-b border-border ${index % 2 === 0 ? 'bg-muted/30' : ''}`}
                    >
                      <td className="p-4 font-medium text-sm text-muted-foreground">
                        {row.label}
                      </td>
                      {uniqueSelectedProducts.map(product => (
                        <td key={product.id} className="p-4 text-center">
                          <span className="text-sm font-medium">
                            {row.getValue(product)}
                          </span>
                        </td>
                      ))}
                    </tr>
                  ))}

                  {/* Action Row */}
                  <tr>
                    <td className="p-4"></td>
                    {uniqueSelectedProducts.map(product => (
                      <td key={product.id} className="p-4 text-center">
                        <Button
                          size="sm"
                          className="w-full"
                          onClick={() => handleViewProduct(product.id)}
                        >
                          Ver detalles
                        </Button>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {uniqueSelectedProducts.length < 2 && (
            <div className="text-center py-12 text-muted-foreground">
              <p className="text-lg">Selecciona al menos 2 productos para comparar</p>
              <p className="text-sm mt-2">Haz clic en los productos de arriba para seleccionarlos</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductComparator;
